<template>
  <div>
    <div class="md:flex md:items-center md:justify-between mb-5">
      <h1 class="text-primary md:font-normal font-sans md:text-mid text-h5 leading-none md:italic md:font-serif md:tracking-tiny uppercase text-center md:text-left md:normal-case font-bold tracking-lg mb-3 md:mb-0">
        {{ $t('Order History') }}
      </h1>
      <button-outline
        class="w-full md:w-auto"
        @click.native="openDownloadModal"
      >
        {{ $t('Download CSV') }}
      </button-outline>
    </div>

    <modal name="modal-netsuite-orders-download" class="netsuite-orders-download-modal">
      <template slot="header">
        <h2 class="pl-4 pr-8 py-4 border-b border-grey-3 text-primary text-h5 uppercase tracking-lg md:normal-case md:italic md:font-serif md:tracking-tiny">
          {{ $t('Download Order History') }}
        </h2>
      </template>
      <template slot="content">
        <div class="p-4 text-left">
          <p class="leading-base tracking-mid mb-md text-dark">
            {{ $t('Optionally choose a date range. Leave blank to download your full order history.') }}
          </p>
          <div class="md:flex md:-mx-2 mb-md">
            <base-input
              class="mb-sm md:mb-0 md:w-1/2 md:px-2"
              type="date"
              name="csvStartDate"
              v-model="csvStartDate"
            >
              {{ $t('Start date') }}
            </base-input>
            <base-input
              class="md:w-1/2 md:px-2"
              type="date"
              name="csvEndDate"
              v-model="csvEndDate"
            >
              {{ $t('End date') }}
            </base-input>
          </div>
          <p
            v-if="downloadError"
            class="text-red leading-base tracking-mid mb-md"
          >
            {{ downloadError }}
          </p>
          <div class="flex flex-col md:flex-row md:justify-end">
            <button-outline
              class="w-full md:w-auto mb-sm md:mb-0 md:mr-sm"
              :disabled="isDownloading"
              @click.native="closeDownloadModal"
            >
              {{ $t('Cancel') }}
            </button-outline>
            <button-full
              class="w-full md:w-auto"
              :loading="isDownloading"
              :disabled="isDownloading || !!dateRangeError"
              @click.native="downloadCsv"
            >
              {{ $t('Download') }}
            </button-full>
          </div>
        </div>
      </template>
    </modal>

    <div class="bg-white lg:py-xl lg:px-8.5 p-md">
      <div class="lg:flex mb-sm justify-between">
        <div v-if="!isLoading && orders.length" class="mb-md lg:mb-0">
          <span>{{ `Items ${from}` }}</span>
          <span>{{ ` to ${to}` }}</span>
        </div>
        <div v-else-if="isLoading" class="mb-md lg:mb-0 skeleton-row items-center hidden lg:flex">
          <span class="skeleton-bone skeleton-bone--sm skeleton-bone--w-md" />
        </div>
        <div class="lg:flex items-center">
          <span class="mr-sm text-h4 leading-base tracking-mid hidden lg:inline">{{ $t('Show') }}</span>
          <base-select
            name="showBy"
            :options="showBy"
            input-class="uppercase order-pagination__select"
            caret-size="icon-xs"
            :selected="String(perPage)"
            v-model="perPage"
          />
          <span class="ml-sm text-h4 leading-base tracking-mid hidden lg:inline">{{ $t('per page') }}</span>
        </div>
      </div>

      <div class="flex row items-center leading-none py-4 px-2 justify-center mb-md">
        <a
          href="#"
          class="mr-sm no-underline text-grey-32 hover:text-black"
          :class="{ 'opacity-25': currentPage === 1 || isLoading }"
          @click.prevent="currentPage === 1 || isLoading ? null : prevPage()"
        >
          <svg class="icon-lg text-black fill-current">
            <use xlink:href="/assets/icons.svg#full-arrow-left" />
          </svg>
          <span class="inline-block text-black text-md mr-md">{{ $t('Newer') }}</span>
        </a>
        <span class="inline-block text-black mr-sm text-md">{{ currentPage }}</span>
        <a
          href="#"
          class="no-underline"
          :class="{ 'opacity-25': !hasMoreOrders || isLoading }"
          @click.prevent="!hasMoreOrders || isLoading ? null : nextPage()"
        >
          <span class="inline-block text-black mr-sm ml-md text-md">{{ $t('Older') }}</span>
          <svg class="icon-lg text-black fill-current">
            <use xlink:href="/assets/icons.svg#full-arrow-right" />
          </svg>
        </a>
      </div>

      <table class="w-full my-10">
        <thead>
          <tr>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm">
              {{ $t('ORDER #') }}
            </th>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden sm:table-cell">
              {{ $t('DATE') }}
            </th>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden md:table-cell">
              {{ $t('TYPE') }}
            </th>
            <th class="text-left font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden md:table-cell">
              {{ $t('OPENING ORDER') }}
            </th>
            <th class="text-right font-normal text-h5 leading-none uppercase tracking-mid border-b border-dark border-solid pb-sm hidden md:table-cell">
              {{ $t('ORDER TOTAL') }}
            </th>
          </tr>
        </thead>
        <tbody v-if="isLoading">
          <tr
            v-for="n in skeletonRowCount"
            :key="`skeleton-${n}`"
            class="skeleton-row"
          >
            <td class="pt-sm pb-xs">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-sm" />
            </td>
            <td class="pt-sm pb-xs hidden sm:table-cell">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-md" />
            </td>
            <td class="pt-sm pb-xs hidden md:table-cell">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-lg" />
            </td>
            <td class="pt-sm pb-xs hidden md:table-cell">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-xs" />
            </td>
            <td class="pt-sm pb-xs text-right hidden md:table-cell">
              <span class="skeleton-bone skeleton-bone--md skeleton-bone--w-xs ml-auto" />
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="!orders.length">
          <tr>
            <td class="w-1/2 py-2" colspan="5">
              <p class="leading-base tracking-mid mb-md my-3">
                {{ $t('Looks like you haven\'t placed any orders.') }}
              </p>
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr
            v-for="order in orders"
            :key="order.id"
          >
            <td class="pt-sm">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ order.etail_id || order.id }}
              </router-link>
            </td>
            <td class="pt-sm hidden sm:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ order.order_date | localeDate }}
              </router-link>
            </td>
            <td class="pt-sm hidden md:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ formatOrderType(order.transaction_type) }}
              </router-link>
            </td>
            <td class="pt-sm hidden md:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="pt-sm text-base leading-sm tracking-xs text-dark underline hover:text-primary2 slow-color-change"
              >
                {{ isOpeningOrder(order) ? $t('Yes') : $t('No') }}
              </router-link>
            </td>
            <td class="pt-sm text-right hidden md:table-cell">
              <router-link
                :to="localizedRoute({ name: 'netsuite-order', params: { transactionId: order.id } })"
                class="text-base leading-sm tracking-xs text-dark italic font-serif underline hover:text-primary2 slow-color-change"
              >
                {{ Number(order.order_total || 0) | price }}
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import debounce from 'lodash-es/debounce'
import i18n from '@vue-storefront/i18n'
import BaseSelect from 'theme/components/core/blocks/Form/BaseSelect'
import BaseInput from 'theme/components/core/blocks/Form/BaseInput'
import Modal from 'theme/components/core/Modal'
import ButtonOutline from 'theme/components/theme/ButtonOutline'
import ButtonFull from 'theme/components/theme/ButtonFull'
import { localeDate } from 'theme/filters/locale-date'

const DOWNLOAD_MODAL = 'modal-netsuite-orders-download'

export default {
  name: 'NetsuiteOrders',
  components: {
    BaseSelect,
    BaseInput,
    Modal,
    ButtonOutline,
    ButtonFull
  },
  filters: {
    localeDate
  },
  data () {
    return {
      currentPage: 1,
      perPage: 25,
      skeletonRowCount: 8,
      csvStartDate: '',
      csvEndDate: '',
      isDownloading: false,
      downloadError: '',
      showBy: [
        { label: '25', value: '25' },
        { label: '50', value: '50' },
        { label: '100', value: '100' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      orders: 'netsuite/getOrderSummaries',
      pagination: 'netsuite/getOrderSummariesPagination',
      isLoading: 'netsuite/isLoading'
    }),
    from () {
      if (!this.orders.length) return 0
      return ((this.currentPage - 1) * Number(this.perPage)) + 1
    },
    to () {
      if (!this.orders.length) return 0
      return ((this.currentPage - 1) * Number(this.perPage)) + this.orders.length
    },
    hasMoreOrders () {
      return this.orders.length >= Number(this.perPage)
    },
    dateRangeError () {
      if (this.csvStartDate && this.csvEndDate && this.csvStartDate > this.csvEndDate) {
        return this.$t('Start date must be on or before end date.')
      }
      return ''
    }
  },
  watch: {
    perPage () {
      this.currentPage = 1
      this.fetchPage()
    },
    dateRangeError (message) {
      this.downloadError = message || ''
    }
  },
  mounted () {
    this.fetchPage()
  },
  methods: {
    fetchPage: debounce(function () {
      this.$store.dispatch('netsuite/fetchOrderSummaries', {
        limit: Number(this.perPage),
        page: this.currentPage,
        orderBy: 'trandate',
        orderByDirection: 'DESC'
      }).catch(() => {})
    }, 250),
    prevPage () {
      if (this.currentPage <= 1) return
      this.currentPage -= 1
      this.fetchPage()
    },
    nextPage () {
      if (!this.hasMoreOrders) return
      this.currentPage += 1
      this.fetchPage()
    },
    formatOrderType (type) {
      if (!type) return ''
      const labels = {
        CustInvc: 'Invoice',
        SalesReceipt: 'Sales Receipt',
        CashSale: 'Sales Receipt',
        Journal: 'Journal',
        CustPymt: 'Payment',
        CustCred: 'Credit Memo',
        CashRfnd: 'Cash Refund',
        Deposit: 'Deposit',
        SalesOrd: 'Sales Order'
      }
      return labels[type] || type
    },
    isOpeningOrder (order) {
      const value = order && order.is_opening_order
      return value === 1 || value === true || value === '1' || value === 'T'
    },
    openDownloadModal () {
      this.downloadError = ''
      this.csvStartDate = ''
      this.csvEndDate = ''
      this.$bus.$emit('modal-show', DOWNLOAD_MODAL)
    },
    closeDownloadModal () {
      if (this.isDownloading) return
      this.$bus.$emit('modal-hide', DOWNLOAD_MODAL)
    },
    async downloadCsv () {
      if (this.isDownloading || this.dateRangeError) return

      this.isDownloading = true
      this.downloadError = ''

      const filters = {}
      if (this.csvStartDate) filters.startDate = this.csvStartDate
      if (this.csvEndDate) filters.endDate = this.csvEndDate

      try {
        const { blob, filename } = await this.$store.dispatch('netsuite/downloadOrderItemsCsv', filters)
        this.triggerBrowserDownload(blob, filename)
        this.$bus.$emit('modal-hide', DOWNLOAD_MODAL)
        this.$store.dispatch('notification/spawnNotification', {
          type: 'success',
          message: i18n.t('Your order history download has started.'),
          action1: { label: i18n.t('OK') }
        })
      } catch (err) {
        this.downloadError = (err && err.message) || this.$t('Unable to download order history. Please try again.')
      } finally {
        this.isDownloading = false
      }
    },
    triggerBrowserDownload (blob, filename) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename || 'order-items.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
  }
}
</script>

<style lang="scss" scoped>
.order-pagination__select {
  @apply bg-grey-27 border-grey-21 normal-case;
  @screen lg {
    @apply py-1 bg-white;
  }
}

.netsuite-orders-download-modal {
  &::v-deep {
    .modal-container {
      @apply bg-white text-left;
      max-width: 32rem;
    }
  }
}

.skeleton-bone {
  @apply block;
  border-radius: 2px;
  height: 0.875rem;
  background: linear-gradient(
    90deg,
    #ebebeb 0%,
    #f7f7f7 40%,
    #ebebeb 80%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s ease-in-out infinite;

  &--sm {
    height: 0.75rem;
  }

  &--md {
    height: 1rem;
  }

  &--w-xs {
    width: 4.5rem;
  }

  &--w-sm {
    width: 6rem;
  }

  &--w-md {
    width: 7.5rem;
  }

  &--w-lg {
    width: 9rem;
  }
}

.skeleton-row {
  .skeleton-bone {
    animation-delay: calc(var(--i, 0) * 80ms);
  }

  &:nth-child(1) .skeleton-bone { --i: 0; }
  &:nth-child(2) .skeleton-bone { --i: 1; }
  &:nth-child(3) .skeleton-bone { --i: 2; }
  &:nth-child(4) .skeleton-bone { --i: 3; }
  &:nth-child(5) .skeleton-bone { --i: 4; }
  &:nth-child(6) .skeleton-bone { --i: 5; }
  &:nth-child(7) .skeleton-bone { --i: 6; }
  &:nth-child(8) .skeleton-bone { --i: 7; }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}
</style>
